/**
 * AI Prof Chatbot — LLM-powered via FastAPI + LiteLLM + Azure GPT-5 nano
 *
 * MESSAGE LIFECYCLE:
 *   • Messages live in React state during the session.
 *   • sessionStorage mirrors them → survive page navigation (React stays mounted).
 *   • sessionStorage is cleared on tab close or hard reload → clean start next visit.
 *
 * STREAMING:
 *   Responses stream token-by-token via SSE. The bot bubble appears immediately
 *   and grows as tokens arrive — no waiting for the full response.
 *
 * SESSION ID:
 *   A UUID is generated once per browser session and stored in sessionStorage.
 *   The backend receives it on every request for future logging or rate-limiting.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Bot, User, AlertCircle } from "lucide-react";
import { CHATBOT_UI } from "../data/home-data";
const botLogo = `${process.env.PUBLIC_URL}/bot-icon.png`;

// ─── Config ──────────────────────────────────────────────────────────────────
const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:8023";

const SESSION_MSG_KEY = "aiprof_chat_messages";   // display messages
const SESSION_HIS_KEY = "aiprof_chat_history";    // API-format history
const SESSION_ID_KEY = "aiprof_session_id";

// ─── Error messages shown in-chat ────────────────────────────────────────────
const ERROR_MESSAGES = CHATBOT_UI.errorMessages;

// ─── Initial greeting messages ───────────────────────────────────────────────
const INITIAL_MESSAGES = [
  {
    id: "init-1",
    role: "bot",
    text: CHATBOT_UI.initialMessages.greeting,
    timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
  },
  {
    id: "init-2",
    role: "bot",
    text: CHATBOT_UI.initialMessages.help,
    timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
  },
];

const QUICK_REPLIES = CHATBOT_UI.quickReplies;

// ─── Session helpers ─────────────────────────────────────────────────────────
function getOrCreateSessionId() {
  let id = sessionStorage.getItem(SESSION_ID_KEY);
  if (!id) {
    id = typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `sess-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    sessionStorage.setItem(SESSION_ID_KEY, id);
  }
  return id;
}

function loadPersistedMessages() {
  try {
    const raw = sessionStorage.getItem(SESSION_MSG_KEY);
    const parsed = raw ? JSON.parse(raw) : INITIAL_MESSAGES;
    // Ensure all loaded messages have a timestamp (for backward compatibility with old sessions)
    return parsed.map(msg => ({
      ...msg,
      timestamp: msg.timestamp || new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }));
  } catch {
    return INITIAL_MESSAGES;
  }
}

function loadPersistedHistory() {
  try {
    const raw = sessionStorage.getItem(SESSION_HIS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

// ─── Render markdown-lite (bold + newlines) ───────────────────────────────────
function renderText(text) {
  if (!text) return null;
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return part.split("\n").map((line, j, arr) => (
      <span key={`${i}-${j}`}>
        {line}
        {j < arr.length - 1 && <br />}
      </span>
    ));
  });
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(loadPersistedMessages);
  const [chatHistory, setChatHistory] = useState(loadPersistedHistory);  // API format
  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [hasNewMessage, setHasNewMessage] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);   // AbortController for in-flight requests

  // ── Persist messages to sessionStorage on every update ──────────────────
  useEffect(() => {
    sessionStorage.setItem(SESSION_MSG_KEY, JSON.stringify(messages));
  }, [messages]);

  useEffect(() => {
    sessionStorage.setItem(SESSION_HIS_KEY, JSON.stringify(chatHistory));
  }, [chatHistory]);

  // ── Scroll to bottom ─────────────────────────────────────────────────────
  useEffect(() => {
    if (open) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, open, isStreaming]);

  // ── Focus input on open ──────────────────────────────────────────────────
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 300);
      setHasNewMessage(false);
    }
  }, [open]);

  // ── Clear chat on page reload ────────────────────────────────────────────
  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.removeItem(SESSION_MSG_KEY);
      sessionStorage.removeItem(SESSION_HIS_KEY);
      sessionStorage.removeItem(SESSION_ID_KEY);
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  // ── Nudge badge after 8 s ────────────────────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => { if (!open) setHasNewMessage(true); }, 8000);
    return () => clearTimeout(t);
  }, []);

  // ── Cleanup abort on unmount ─────────────────────────────────────────────
  useEffect(() => () => abortRef.current?.abort(), []);

  const sendMessage = useCallback(async (text) => {
    const trimmed = (text || input).trim();
    if (!trimmed || isStreaming) return;

    if (trimmed.length > 1000) {
      const userMsg = {
        id: `u-${Date.now()}`,
        role: "user",
        text: trimmed,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      const botMsg = {
        id: `b-${Date.now()}`,
        role: "bot",
        text: ERROR_MESSAGES.message_too_long,
        isError: true,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      setMessages(prev => [...prev, userMsg, botMsg]);
      setInput("");
      return;
    }

    // Abort any previous in-flight request
    abortRef.current?.abort();
    abortRef.current = new AbortController();

    // 1. Add user message to display
    const userMsg = {
      id: `u-${Date.now()}`,
      role: "user",
      text: trimmed,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setInput("");
    setIsStreaming(true);

    // 2. Build API history entry
    const updatedHistory = [...chatHistory, { role: "user", content: trimmed }];
    setChatHistory(updatedHistory);

    // 3. Add empty bot bubble — we'll fill it as tokens stream in
    const botMsgId = `b-${Date.now()}`;
    setMessages(prev => [...prev, {
      id: botMsgId,
      role: "bot",
      text: "",
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }]);

    let botText = "";

    try {
      const res = await fetch(`${BACKEND_URL}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        signal: abortRef.current.signal,
        body: JSON.stringify({
          session_id: getOrCreateSessionId(),
          messages: updatedHistory,
          page_context: window.location.pathname,
        }),
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      // 4. Read SSE stream
      const reader = res.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const raw = decoder.decode(value, { stream: true });
        const lines = raw.split("\n");

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6).trim();
          if (payload === "[DONE]") break;

          try {
            const parsed = JSON.parse(payload);

            if (parsed.token) {
              botText += parsed.token;
              // Update the bot bubble in place as tokens arrive
              setMessages(prev =>
                prev.map(m => m.id === botMsgId ? { ...m, text: botText } : m)
              );
            }

            if (parsed.error) {
              const friendly = ERROR_MESSAGES[parsed.error] || ERROR_MESSAGES.server_error;
              setMessages(prev =>
                prev.map(m => m.id === botMsgId ? { ...m, text: friendly, isError: true } : m)
              );
              botText = friendly;
            }
          } catch {
            // Ignore malformed SSE lines
          }
        }
      }

      // 5. Commit final bot text to API history
      if (botText) {
        setChatHistory(prev => [...prev, { role: "assistant", content: botText }]);
      }

    } catch (err) {
      if (err.name === "AbortError") return; // User navigated away — fine

      // Revert the last user message from API history since the request failed
      setChatHistory(chatHistory);

      const friendly = err.message.includes("429")
        ? ERROR_MESSAGES.rate_limit
        : err.message.includes("400")
          ? ERROR_MESSAGES.message_too_long
          : err.message.includes("fetch")
            ? ERROR_MESSAGES.network_error
            : ERROR_MESSAGES.server_error;

      setMessages(prev =>
        prev.map(m => m.id === botMsgId ? { ...m, text: friendly, isError: true } : m)
      );
    } finally {
      setIsStreaming(false);
    }
  }, [input, messages, chatHistory, isStreaming]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <>
      {/* Toggle button */}
      <button
        id="chatbot-toggle-btn"
        onClick={() => setOpen(o => !o)}
        className="fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full bg-aiblue hover:bg-aiorange text-white shadow-2xl shadow-aiblue/40 flex items-center justify-center transition-all duration-300 cta-pulse"
        aria-label={open ? "Close chat" : "Open chat"}
      >
        <AnimatePresence mode="wait">
          {open ? (
            <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.2 }}>
              <X className="h-6 w-6" />
            </motion.span>
          ) : (
            <motion.span key="chat" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.2 }}>
              <MessageCircle className="h-6 w-6" />
            </motion.span>
          )}
        </AnimatePresence>

        {/* New-message nudge badge */}
        {!open && hasNewMessage && (
          <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-aiorange border-2 border-white animate-bounce" />
        )}
      </button>

      {/* Chat window */}
      <AnimatePresence>
        {open && (
          <motion.div
            id="chatbot-window"
            key="chatbot"
            initial={{ opacity: 0, y: 24, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 400, damping: 30 }}
            className="fixed left-4 right-4 sm:left-auto sm:right-6 z-50 w-auto sm:w-96 flex flex-col rounded-2xl overflow-hidden shadow-2xl border border-gray-100"
            style={{ top: "8.5rem", bottom: "6rem" }}
          >
            {/* Header */}
            <div className="flex items-center gap-3 px-4 py-3 bg-aiblue text-white flex-shrink-0">
              <div className="h-10 w-10 rounded-full bg-white flex items-center justify-center overflow-hidden shadow-sm border border-white/20">
                <img src={botLogo} alt="Bot Avatar" className="h-6 w-6 object-contain -translate-x-[2px] translate-y-[2px]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm leading-tight">{CHATBOT_UI.headerTitle}</p>
                <p className="text-xs text-white/70 flex items-center gap-1">
                  {/* <span className={`inline-block h-2 w-2 rounded-full border-2 border-white/20 ${isStreaming ? "bg-yellow-300" : "bg-green-400 shadow-[0_0_6px_#4ade80]"}`} /> */}
                  {/* {isStreaming ? "Thinking…" : "Online · Typically replies instantly"} */}
                </p>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto bg-gray-50 px-4 py-3 space-y-3">
              {messages.map((msg) => {
                // Hide the empty bubble while waiting for the first token
                if (msg.role === "bot" && isStreaming && msg.text === "" && messages[messages.length - 1]?.id === msg.id) {
                  return null;
                }
                return (
                  <motion.div
                    key={msg.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                    className={`flex items-end gap-2 ${msg.role === "user" ? "flex-row-reverse" : "flex-row"}`}
                  >
                    {/* Avatar */}
                    <div className={`flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center overflow-hidden ${msg.isError ? "bg-red-400 text-white" : msg.role === "bot" ? "bg-white shadow-sm border border-gray-200" : "bg-aiorange text-white"
                      }`}>
                      {msg.isError
                        ? <AlertCircle className="h-4 w-4" />
                        : msg.role === "bot"
                          ? <img src={botLogo} alt="Bot Avatar" className="h-5 w-5 object-contain -translate-x-[1px] translate-y-[1px]" />
                          : <User className="h-4 w-4" />
                      }
                    </div>

                    {/* Bubble */}
                    <div className={`max-w-[75%] flex flex-col rounded-2xl px-3 py-2 text-sm leading-relaxed ${msg.role === "bot"
                      ? msg.isError
                        ? "bg-red-50 text-red-700 border border-red-200 rounded-tl-sm"
                        : "bg-white text-gray-800 border border-gray-100 shadow-sm rounded-tl-sm"
                      : "bg-aiblue text-white rounded-tr-sm"
                      }`}>
                      <div>
                        {renderText(msg.text)}
                        {/* Blinking cursor while streaming this bubble */}
                        {msg.role === "bot" && isStreaming && msg.text !== undefined && msg.text !== "" && messages[messages.length - 1]?.id === msg.id && (
                          <span className="inline-block w-0.5 h-3.5 bg-aiblue ml-0.5 animate-pulse align-middle" />
                        )}
                      </div>
                      {/* Timestamp */}
                      <span className={`text-[10px] mt-1 self-end ${msg.role === 'bot' ? 'text-gray-400' : 'text-blue-200'}`}>
                        {msg.timestamp}
                      </span>
                    </div>
                  </motion.div>
                );
              })}

              {/* Typing indicator (shown before first token arrives) */}
              {isStreaming && messages[messages.length - 1]?.text === "" && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-end gap-2"
                >
                  <div className="flex-shrink-0 h-8 w-8 rounded-full bg-white border border-gray-200 shadow-sm flex items-center justify-center overflow-hidden">
                    <img src={botLogo} alt="Bot Avatar" className="h-5 w-5 object-contain -translate-x-[1px] translate-y-[1px]" />
                  </div>
                  <div className="bg-white border border-gray-100 shadow-sm rounded-2xl rounded-tl-sm px-4 py-3">
                    <div className="flex gap-1 items-center">
                      <span className="h-2 w-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                      <span className="h-2 w-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: "150ms" }} />
                      <span className="h-2 w-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: "300ms" }} />
                    </div>
                  </div>
                </motion.div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Quick replies (only before first user message) */}
            {messages.filter(m => m.role === "user").length === 0 && !isStreaming && (
              <div className="bg-gray-50 px-4 pb-2 flex flex-wrap gap-2 flex-shrink-0">
                {QUICK_REPLIES.map((qr) => (
                  <button
                    key={qr}
                    onClick={() => sendMessage(qr)}
                    className="text-xs bg-white border border-aiblue/30 text-aiblue hover:bg-aiblue hover:text-white rounded-full px-3 py-1 transition-all duration-200 whitespace-nowrap"
                  >
                    {qr}
                  </button>
                ))}
              </div>
            )}

            {/* Input */}
            <div className="bg-white border-t border-gray-100 px-3 py-2 flex items-center gap-2 flex-shrink-0">
              <input
                ref={inputRef}
                id="chatbot-input"
                type="text"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={isStreaming ? CHATBOT_UI.inputPlaceholderStreaming : CHATBOT_UI.inputPlaceholder}
                disabled={isStreaming}
                className="flex-1 text-sm bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 outline-none focus:border-aiblue focus:ring-2 focus:ring-aiblue/20 transition-all disabled:opacity-60"
                maxLength={1005}
              />
              <button
                id="chatbot-send-btn"
                onClick={() => sendMessage()}
                disabled={!input.trim() || isStreaming}
                className="h-9 w-9 rounded-xl bg-aiblue hover:bg-aiorange disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center text-white transition-all duration-200 flex-shrink-0"
                aria-label="Send message"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
